import { FC, useContext } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Grid, Stack, useResponsive } from "capitalrx-components-2.0/lib/layout";
import { Paragraph, Subtitle } from "capitalrx-components-2.0/lib/typography";

import { ReactComponent as CostcoLogo } from "@/App/components/MemberCard/Assets/costco-rx-logo.svg";
import { StackOrShelf } from "@/App/components/StackOrShelf/StackOrShelf";
import { ThemedButton } from "@/App/components/themed/Button";
import { ExperienceContext } from "@/App/context";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";

const TRANSLATION_PATH = "DASHBOARD.recentActivityDashboard.additionalServices";

const ServiceTile = styled.div`
  border-radius: 8px;
  border: 1px solid #cfd9e1;
  padding: 24px;
  height: 100%;
  width: 100%;
`;

export const AdditionalServicesTiles: FC = () => {
  const { t: translate } = useTranslation();
  const { hideCostcoHomeDeliveryCard, hideCostcoSpecialtyCard } = useContext(ExperienceContext);

  const { halfWidthColumns, logoHeight, logoWidth } = useResponsive({
    logoHeight: {
      desktop: "24",
      tablet: "24",
      mobile: "18",
    },
    logoWidth: {
      desktop: "80",
      tablet: "80",
      mobile: "60",
    },
    halfWidthColumns: {
      desktop: 6,
      tablet: 12,
      mobile: 12,
    },
  });

  const showSpecialty = !hideCostcoSpecialtyCard;
  const showMailOrder = !hideCostcoHomeDeliveryCard;

  if (!showSpecialty && !showMailOrder) {
    return null;
  }

  return (
    <Stack spacing="medium">
      <Subtitle testId="additional-services-title">{translate(`${TRANSLATION_PATH}.title`)}</Subtitle>
      <Grid>
        {showSpecialty && (
          <Grid.Span columns={halfWidthColumns}>
            <ServiceTile data-testid="costco-specialty-tile">
              <Stack alignment="left" style={{ height: "100%" }}>
                <StackOrShelf alignment="center" spacing="small">
                  <Subtitle>{translate(`${TRANSLATION_PATH}.specialtyServiceWith`)}</Subtitle>
                  <CostcoLogo height={logoHeight} width={logoWidth} viewBox="0 0 277 81" />
                </StackOrShelf>
                <Stack.Spacer />
                <Paragraph>{translate(`${TRANSLATION_PATH}.specialtyDescription`)}</Paragraph>
                <Stack.Spacer />
                <ThemedButton
                  testId="costco-specialty-external-connection"
                  icon="link-out"
                  appearance="secondary"
                  onClick={() => {
                    logEvent(ANALYTIC_EVENTS.DASHBOARD_NAV + "_external_link", { path: location });
                    window.open("https://www.lumicera.com/costco-specialty-pharmacy/", "_blank", "noopener,noreferrer");
                  }}
                >
                  {translate(`${TRANSLATION_PATH}.visitCostcoSpecialty`)}
                </ThemedButton>
              </Stack>
            </ServiceTile>
          </Grid.Span>
        )}
        {showMailOrder && (
          <Grid.Span columns={halfWidthColumns}>
            <ServiceTile data-testid="costco-mail-order-tile">
              <Stack alignment="left" style={{ height: "100%" }}>
                <StackOrShelf alignment="center" spacing="small">
                  <Subtitle>{translate(`${TRANSLATION_PATH}.mailOrderWith`)}</Subtitle>
                  <CostcoLogo height={logoHeight} width={logoWidth} viewBox="0 0 277 81" />
                </StackOrShelf>
                <Stack.Spacer />
                <Paragraph>
                  {translate(`${TRANSLATION_PATH}.mailOrderDescription`)}
                </Paragraph>
                <Stack.Spacer />
                <ThemedButton
                  testId="costco-mail-order-external-connection"
                  icon="link-out"
                  appearance="secondary"
                  onClick={() => {
                    logEvent(ANALYTIC_EVENTS.DASHBOARD_NAV + "_external_link", { path: location });
                    window.open("https://rx.costco.com", "_blank", "noopener,noreferrer");
                  }}
                >
                  {translate(`${TRANSLATION_PATH}.visitCostcoPharmacy`)}
                </ThemedButton>
              </Stack>
            </ServiceTile>
          </Grid.Span>
        )}
      </Grid>
    </Stack>
  );
};
